import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { alTerminarLaSesion } from '../api/cliente.js';
import { borrarTodosLosBorradores } from '../borradores/almacenDeBorradores.js';
import {
  cerrarSesion as cerrarSesionEnServidor,
  consultarConfiguracionInicial,
  obtenerContextoDeSesion,
} from '../api/nucleoApi.js';
import { obtenerFechaDeNegocioHoy } from '../fecha/fechaLocal.js';

const ContextoDeSesion = createContext(null);

/**
 * Contexto de sesion del shell (contratos.md, ObtenerContextoDeSesion).
 *
 * Se resuelve UNA vez al arrancar, antes de montar cualquier modulo. La
 * fechaLocal que se manda es la del DISPOSITIVO: el servidor no resuelve "hoy"
 * (D-06, RF-013).
 *
 * D-09: el shell NO sondea el estado de la sesion con un temporizador ni mantiene
 * un canal abierto. Basta la revalidacion por peticion: en cuanto el dispositivo
 * hace cualquier llamada recibe SESION_REQUERIDA y sale (RN-046b).
 */
export function ProveedorDeSesion({ children }) {
  const navegar = useNavigate();
  const [estado, setEstado] = useState({
    cargando: true,
    sesion: null,
    requiereConfiguracionInicial: false,
    error: null,
  });

  const cargar = useCallback(async () => {
    setEstado((anterior) => ({ ...anterior, cargando: true }));
    try {
      const inicial = await consultarConfiguracionInicial();
      if (inicial.requiereConfiguracionInicial) {
        setEstado({ cargando: false, sesion: null, requiereConfiguracionInicial: true, error: null });
        return;
      }

      const sesion = await obtenerContextoDeSesion(obtenerFechaDeNegocioHoy());
      setEstado({ cargando: false, sesion, requiereConfiguracionInicial: false, error: null });
    } catch (error) {
      // Sin sesion no es un error visible: el shell lleva a la pantalla de acceso.
      setEstado({
        cargando: false,
        sesion: null,
        requiereConfiguracionInicial: false,
        error: error.codigo === 'SESION_REQUERIDA' ? null : error,
      });
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Fin de sesion detectado por el cliente de API en cualquier peticion.
  useEffect(() => alTerminarLaSesion((error) => {
    setEstado((anterior) => ({ ...anterior, sesion: null }));
    // Si el motivo es la desactivacion, primero se explica lo ocurrido; si solo
    // caduco, se va directo al acceso sin mensaje de error (RN-046c).
    navegar(error.esAccesoDesactivado ? '/sesion-finalizada' : '/acceso', { replace: true });
  }), [navegar]);

  const establecerSesion = useCallback((sesion) => {
    setEstado({ cargando: false, sesion, requiereConfiguracionInicial: false, error: null });
  }, []);

  const cerrar = useCallback(async () => {
    try {
      await cerrarSesionEnServidor();
    } finally {
      // RN-134c: al cerrar sesión no sobrevive ningún borrador de captura, aunque
      // no hayan pasado sus 24 horas ("nada de la sesión sobrevive al cierre",
      // RN-045d). Barre SOLO las claves de borrador.
      borrarTodosLosBorradores();
      // La preferencia de tema NO se borra: no es dato de negocio (RN-045d).
      setEstado({ cargando: false, sesion: null, requiereConfiguracionInicial: false, error: null });
      navegar('/acceso', { replace: true });
    }
  }, [navegar]);

  const valor = useMemo(() => ({
    ...estado,
    haySesion: Boolean(estado.sesion),
    recargar: cargar,
    establecerSesion,
    cerrarSesion: cerrar,
  }), [estado, cargar, establecerSesion, cerrar]);

  return <ContextoDeSesion.Provider value={valor}>{children}</ContextoDeSesion.Provider>;
}

export function useSesion() {
  const contexto = useContext(ContextoDeSesion);
  if (!contexto) throw new Error('useSesion debe usarse dentro de ProveedorDeSesion.');
  return contexto;
}
